import {Capacitor} from '@capacitor/core';
import {Preferences} from '@capacitor/preferences';
import Cookies from 'js-cookie';

import {trackEvent} from '#Client/components/util';

const SAVED_DATA_VERSION = 1;
export const SAVED_DATA_STORAGE_KEY = 'SAVED_DATA';
type PuzzleState = Record<string, string>;

export const backgroundColors = [
  'letters_red',
  'letters_orange',
  'letters_yellow',
  'letters_green',
  'letters_dark_blue',
  'letters_light_blue',
  'letters_purple',
  'letters_gray',
  'letters_black',
  'solid_red',
  'solid_orange',
  'solid_yellow',
  'solid_green',
  'solid_dark_blue',
  'solid_light_blue',
  'solid_purple',
  'solid_gray',
  'solid_black',
] as const;

export type BackgroundColor = (typeof backgroundColors)[number];

export type SavedData = {
  backgroundColor?: BackgroundColor;
  userId?: string;
  userName?: string;
  version?: number;
  difficulty?: number;
  viewedNewsIndex?: number;
  data?: {
    dateString: string;
    puzzleStates: Record<string, PuzzleState>;
  };
};

export const saveObject = async (object: SavedData) => {
  trackEvent('passive', 'saved-data', 'saved', 'cookies');
  const stringifiedObject = JSON.stringify(object);
  if (Capacitor.isNativePlatform()) {
    await Preferences.set({
      key: SAVED_DATA_STORAGE_KEY,
      value: stringifiedObject,
    });
    return;
  }
  localStorage.setItem(SAVED_DATA_STORAGE_KEY, stringifiedObject);
  Cookies.set(SAVED_DATA_STORAGE_KEY, stringifiedObject, {
    expires: 3650,
  });
};

const createBlankSavedData = async (): Promise<SavedData> => {
  trackEvent('passive', 'saved-data', 'created', 'new user');
  const savedData: SavedData = {
    version: SAVED_DATA_VERSION,
  };
  await saveObject(savedData);
  return savedData;
};

export const getSavedData = async (): Promise<SavedData> => {
  if (Capacitor.isNativePlatform()) {
    const {value} = await Preferences.get({key: SAVED_DATA_STORAGE_KEY});
    if (value) {
      return JSON.parse(value);
    }
    return createBlankSavedData();
  }
  const savedDataStringLocalStorage = localStorage.getItem(
    SAVED_DATA_STORAGE_KEY,
  );
  if (savedDataStringLocalStorage) {
    trackEvent('passive', 'saved-data', 'retrieved', 'localStorage');
    return JSON.parse(savedDataStringLocalStorage);
  }
  const savedDataStringCookies = Cookies.get(SAVED_DATA_STORAGE_KEY);
  if (typeof savedDataStringCookies === 'string') {
    trackEvent('passive', 'saved-data', 'retrieved', 'cookies');
    return JSON.parse(savedDataStringCookies);
  }
  return createBlankSavedData();
};

export const isUserNew = async (): Promise<boolean> => {
  if (Capacitor.isNativePlatform()) {
    const {value} = await Preferences.get({key: SAVED_DATA_STORAGE_KEY});
    return !value;
  }
  return (
    !localStorage.getItem(SAVED_DATA_STORAGE_KEY) &&
    !Cookies.get(SAVED_DATA_STORAGE_KEY)
  );
};
